import { FormsModule } from '@angular/forms';
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ReportDetail } from '../report.manager.model';
import { Constants } from '../../../constants.new';
import * as _ from 'lodash';
import { ReportType } from '../report-type';
import { Picklist } from 'src/app/Tax-Rate/Tax-Authority/tax.authority.component';
import { AttachmentService } from 'src/app/Attachment/attachment.service';
import { ALL_PROPERTY_TYPES } from 'src/app/Attachment/Bundler/attachmentBundler.service';

@Component({
    selector: 'further-limit-panel-attachment-bundler',
    templateUrl: './further.limit.panel.attachment.bundler.component.html',
    imports: [FormsModule],
})
export class FurtherLimitPanelAttachmentBundlerComponent implements OnInit, OnChanges {
    constructor(
        private constants: Constants,
        private readonly _attachmentService: AttachmentService,
    ) {}

    @Input() report: ReportDetail;
    @Input() availableYears: number[] = [];
    @Input() filterScope: ReportType = ReportType.AttachmentBundlerCompany;
    @Input() entityTypeFilters: Core.EntityTypes[] = [];
    @Input() disabled: boolean = false;
    propertyTypes: any[] = [];
    attachmentLocationOptions: Picklist[] = [];
    attachmentTypeOptions: Picklist[] = [];
    allAttachmentTypes: Core.AttachmentTypeModel[] = [];
    areAttachmentLocationOptionsLoading: boolean = false;
    areAttachmentTypeOptionsLoading: boolean = false;
    showIncludeBlankTaxYearCheckbox: boolean = false;

    async ngOnInit() {
        this.setPropertyTypeDefaultsForScope();
        this.report.criteria.attachmentTypes = this.report.criteria.attachmentTypes || [];
        this.report.criteria.attachmentLocations = this.report.criteria.attachmentLocations || [];
        this.report.criteria.ignoreConditionalFilterOptions =
            this.report.criteria.ignoreConditionalFilterOptions ?? false;

        this.propertyTypes = _.map(this.constants.PropertyTypeNames, (name: string, id: number) => {
            return {
                name: name,
                propertyTypeId: Number(id),
            };
        });

        this.setAttachmentLocationOptions();

        this.allAttachmentTypes = await this._attachmentService.getAttachmentTypes();
        this.setAttachmentTypeOptions();

        this.setShowIncludeBlankTaxYearCheckbox();
        this.setIncludeBlankTaxYear();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['entityTypeFilters'] && !changes['entityTypeFilters'].isFirstChange()) {
            this.setAttachmentLocationOptions();
            this.setAttachmentTypeOptions();
            this.setShowIncludeBlankTaxYearCheckbox();
            this.setIncludeBlankTaxYear();
        }
    }

    yearChangedValidation(): void {
        if (this.report.criteria.taxYearBegin > this.report.criteria.taxYearEnd) {
            this.report.criteria.taxYearEnd = null;
        }
    }

    shouldShowPropertyTypeSelector(): boolean {
        return this.filterScope !== ReportType.AttachmentBundlerJurisdiction;
    }

    isPropertyTypeSelectorEnabled(): boolean {
        const allowedAttachmentLocations = [
            Core.AttachmentBundlerLocationEnum.Parcel,
            Core.AttachmentBundlerLocationEnum.PPReturn,
            Core.AttachmentBundlerLocationEnum.AssessmentRevision,
            Core.AttachmentBundlerLocationEnum.Appeal,
            Core.AttachmentBundlerLocationEnum.TaxBill,
            Core.AttachmentBundlerLocationEnum.DocumentIntake,
            Core.AttachmentBundlerLocationEnum.Refund,
            Core.AttachmentBundlerLocationEnum.Budget,
        ];

        const unallowedSelectedAttachmentLocations = this.report.criteria.attachmentLocations.filter(
            (v) => !allowedAttachmentLocations.includes(v),
        );

        return (
            this.report.criteria.ignoreConditionalFilterOptions ||
            (this.report.criteria.attachmentLocations.length != 0 && unallowedSelectedAttachmentLocations.length == 0)
        );
    }

    onIgnoreConditionalFilterOptionsChanged(): void {
        this.setAttachmentLocationOptions();
        this.setAttachmentTypeOptions();
    }

    setAttachmentLocationOptions(): void {
        this.areAttachmentLocationOptionsLoading = true;
        let filteredLocations = this.constants.AttachmentBundlerLocations.filter((x) => x.filter == this.filterScope);

        if (!this.report.criteria.ignoreConditionalFilterOptions) {
            for (const entityType of this.entityTypeFilters) {
                switch (entityType) {
                    case Core.EntityTypes.Site:
                        //When Sites are selected, hide Company, Filing Batch, and Payment Batch.
                        filteredLocations = filteredLocations.filter(
                            (x) =>
                                x.id != Core.AttachmentBundlerLocationEnum.Company &&
                                x.id != Core.AttachmentBundlerLocationEnum.FilingBatch &&
                                x.id != Core.AttachmentBundlerLocationEnum.PaymentBatch,
                        );
                        break;

                    case Core.EntityTypes.Parcel:
                        //When Parcels are selected, hide Company, Site, Filing Batch, Invoice, Payment Batch
                        filteredLocations = filteredLocations.filter(
                            (x) =>
                                x.id != Core.AttachmentBundlerLocationEnum.Company &&
                                x.id != Core.AttachmentBundlerLocationEnum.Site &&
                                x.id != Core.AttachmentBundlerLocationEnum.FilingBatch &&
                                x.id != Core.AttachmentBundlerLocationEnum.Invoice &&
                                x.id != Core.AttachmentBundlerLocationEnum.PaymentBatch,
                        );
                        break;
                    case Core.EntityTypes.State:
                        //When States are selected, hide Company and Payment Batch.
                        filteredLocations = filteredLocations.filter(
                            (x) =>
                                x.id != Core.AttachmentBundlerLocationEnum.Company &&
                                x.id != Core.AttachmentBundlerLocationEnum.PaymentBatch,
                        );
                        break;

                    case Core.EntityTypes.Assessor:
                        //When Assessors are selected, hide Company, Site, Filing Batch, Invoice, and Payment Batch.
                        filteredLocations = filteredLocations.filter(
                            (x) =>
                                x.id != Core.AttachmentBundlerLocationEnum.Company &&
                                x.id != Core.AttachmentBundlerLocationEnum.Site &&
                                x.id != Core.AttachmentBundlerLocationEnum.FilingBatch &&
                                x.id != Core.AttachmentBundlerLocationEnum.Invoice &&
                                x.id != Core.AttachmentBundlerLocationEnum.PaymentBatch,
                        );
                        break;

                    case Core.EntityTypes.Collector:
                        //When Collectors are selected, hide State, Assessor, and Form Revision.
                        filteredLocations = filteredLocations.filter(
                            (x) =>
                                x.id != Core.AttachmentBundlerLocationEnum.State &&
                                x.id != Core.AttachmentBundlerLocationEnum.Assessor &&
                                x.id != Core.AttachmentBundlerLocationEnum.FormRevision,
                        );
                        break;
                }
            }
        }

        const result = _.map(filteredLocations, (x) => ({ label: x.label, id: x.id }) as Picklist);

        this.attachmentLocationOptions = _.chain(result).uniqBy('id').sortBy('label').value();

        this.removeInvalidSelectedAttachmentLocations();

        this.areAttachmentLocationOptionsLoading = false;
    }

    removeInvalidSelectedAttachmentLocations(): void {
        if (this.report.criteria.attachmentLocations.length === 0) {
            return;
        }

        const validIds = _.map(this.attachmentLocationOptions, 'id');
        this.report.criteria.attachmentLocations = this.report.criteria.attachmentLocations.filter((loc) =>
            validIds.includes(loc),
        );
    }

    onSelectedAttachmentLocationsChanged(e): void {
        this.setAttachmentTypeOptions();
        this.setShowIncludeBlankTaxYearCheckbox();
        this.setIncludeBlankTaxYear();

        if (!this.isPropertyTypeSelectorEnabled()) {
            this.report.criteria.propertyTypeIDs = this.shouldShowPropertyTypeSelector() ? [...ALL_PROPERTY_TYPES] : [];
        }
    }

    setShowIncludeBlankTaxYearCheckbox() {
        const allowedAttachmentLocations = [
            Core.AttachmentBundlerLocationEnum.Company,
            Core.AttachmentBundlerLocationEnum.Site,
            Core.AttachmentBundlerLocationEnum.Parcel,
            Core.AttachmentBundlerLocationEnum.PaymentBatch,
            Core.AttachmentBundlerLocationEnum.DocumentIntake,
            Core.AttachmentBundlerLocationEnum.State,
            Core.AttachmentBundlerLocationEnum.Assessor,
            Core.AttachmentBundlerLocationEnum.FormRevision,
            Core.AttachmentBundlerLocationEnum.Collector,
            Core.AttachmentBundlerLocationEnum.TaxRateArea,
            Core.AttachmentBundlerLocationEnum.TaxAuthority,
        ];

        const unallowedSelectedAttachmentLocations = this.report.criteria.attachmentLocations.filter(
            (v) => !allowedAttachmentLocations.includes(v),
        );

        this.showIncludeBlankTaxYearCheckbox =
            this.report.criteria.attachmentLocations.length != 0 && unallowedSelectedAttachmentLocations.length == 0;

        this.setAttachmentTypeOptions();

        if (!this.isPropertyTypeSelectorEnabled()) {
            this.report.criteria.propertyTypeIDs = this.shouldShowPropertyTypeSelector() ? [...ALL_PROPERTY_TYPES] : [];
        }
    }

    setIncludeBlankTaxYear() {
        if (!this.showIncludeBlankTaxYearCheckbox) {
            this.report.criteria.includeBlankTaxYear = false;
        }
    }

    selectAllAttachmentLocations(): void {
        this.report.criteria.attachmentLocations = _.map(this.attachmentLocationOptions, 'id');
        this.setAttachmentTypeOptions();
        this.setShowIncludeBlankTaxYearCheckbox();
        this.setIncludeBlankTaxYear();
    }

    unselectAllAttachmentLocations(): void {
        this.report.criteria.attachmentLocations = [];
        this.report.criteria.attachmentTypes = [];
        this.setShowIncludeBlankTaxYearCheckbox();
        this.setIncludeBlankTaxYear();
    }

    setAttachmentTypeOptions() {
        this.areAttachmentTypeOptionsLoading = true;
        const result: Picklist[] = [];

        this.report.criteria.attachmentLocations.forEach((selectedLocation) => {
            this.allAttachmentTypes.forEach((type) => {
                if (
                    this.checkAllowType(type, selectedLocation) ||
                    this.report.criteria.ignoreConditionalFilterOptions
                ) {
                    const option = {
                        id: type.attachmentTypeID,
                        label: `${type.attachmentCategoryName} - ${type.attachmentTypeName}`,
                    };
                    result.push(option);
                }
            });
        });

        this.attachmentTypeOptions = _.chain(result).uniqBy('id').sortBy('label').value();

        if (this.report.criteria.attachmentCategories && this.report.criteria.attachmentCategories.length > 0) {
            const typesInCategories = this.allAttachmentTypes
                .filter((type) => this.report.criteria.attachmentCategories.includes(type.attachmentCategoryID))
                .map((type) => type.attachmentTypeID);

            const availableOptionIds = this.attachmentTypeOptions.map((o) => o.id);
            this.report.criteria.attachmentTypes = typesInCategories.filter((id) => availableOptionIds.includes(id));
        } else {
            this.removeInvalidSelectedAttachmentTypes();
        }

        this.areAttachmentTypeOptionsLoading = false;
    }

    removeInvalidSelectedAttachmentTypes() {
        const itemsToRemove: Core.AttachmentTypes[] = [];
        const attachmentTypeOptionIds = _.map(this.attachmentTypeOptions, 'id');

        if (this.report.criteria.attachmentTypes.length > 0 && attachmentTypeOptionIds.length > 0) {
            this.report.criteria.attachmentTypes.forEach((type) => {
                if (!attachmentTypeOptionIds.includes(type)) {
                    itemsToRemove.push(type);
                }
            });

            this.report.criteria.attachmentTypes = this.report.criteria.attachmentTypes.filter(
                (x) => !itemsToRemove.includes(x),
            );
        }
    }

    selectAllAttachmentTypes(): void {
        this.report.criteria.attachmentTypes = _.map(this.attachmentTypeOptions, 'id') as number[];
    }

    unselectAllAttachmentTypes(): void {
        this.report.criteria.attachmentTypes = [];
    }

    checkAllowType(type: Core.AttachmentTypeModel, selectedLocation: Core.AttachmentBundlerLocationEnum): boolean {
        switch (selectedLocation) {
            case Core.AttachmentBundlerLocationEnum.Company:
                return type.allowAtCompany;
            case Core.AttachmentBundlerLocationEnum.Site:
                return type.allowAtSite;
            case Core.AttachmentBundlerLocationEnum.Parcel:
                return type.allowAtParcel;
            case Core.AttachmentBundlerLocationEnum.PPReturn:
                return type.allowAtPPReturn;
            case Core.AttachmentBundlerLocationEnum.AssessmentRevision:
                return type.allowAtAssessment;
            case Core.AttachmentBundlerLocationEnum.Appeal:
                return type.allowAtAppeal;
            case Core.AttachmentBundlerLocationEnum.TaxBill:
                return type.allowAtTaxBill;
            case Core.AttachmentBundlerLocationEnum.TaxPayment:
                return type.allowAtTaxPayment;
            case Core.AttachmentBundlerLocationEnum.Refund:
                return type.allowAtRefund;
            case Core.AttachmentBundlerLocationEnum.Budget:
                return type.allowAtBudget;
            case Core.AttachmentBundlerLocationEnum.FilingBatch:
                return type.allowAtFilingBatch;
            case Core.AttachmentBundlerLocationEnum.Invoice:
                return type.allowAtInvoice;
            case Core.AttachmentBundlerLocationEnum.PaymentBatch:
                return type.allowAtPaymentBatch;
            case Core.AttachmentBundlerLocationEnum.DocumentIntake:
                return type.isDocumentIntakeSupported;
            case Core.AttachmentBundlerLocationEnum.State:
                return type.allowAtState;
            case Core.AttachmentBundlerLocationEnum.Assessor:
                return type.allowAtAssessor;
            case Core.AttachmentBundlerLocationEnum.FormRevision:
                return type.allowAtFormRevision;
            case Core.AttachmentBundlerLocationEnum.Collector:
                return type.allowAtCollector;
            case Core.AttachmentBundlerLocationEnum.TaxRateArea:
                return type.allowAtTaxRateArea;
            case Core.AttachmentBundlerLocationEnum.TaxAuthority:
                return type.allowAtTaxAuthority;
            default:
                return false;
        }
    }

    private setPropertyTypeDefaultsForScope(): void {
        if (this.shouldShowPropertyTypeSelector()) {
            this.report.criteria.propertyTypeIDs = this.report.criteria.propertyTypeIDs || [...ALL_PROPERTY_TYPES];
            return;
        }

        this.report.criteria.propertyTypeIDs = [];
    }
}
